import BehaviorConfig from '@/models/app-config/behavior/behavior'

/**
 * カラー絞り込みの選択肢1つ分。iconNameはグローバルCSSの
 * `contact-lens-color-icon--{iconName}`(色のイメージを円で表現するアイコン)に対応する。
 */
export interface ColorFamilyOption {
  readonly value: string
  readonly label: string
  readonly iconName: string
}

/**
 * カラー系統名 → ユーザー向けラベル。
 * 既存のカラコン検索(SearchContactLens)の絞り込みUI(SearchSysCondCommon.vue)と同じ語彙。
 * suikouはカラー一覧には出さない(絞り込みモーダルではカラーオプションの「水光」= onlySuikou で扱う)が、
 * URLにcolorFamilies=suikouが来たときのチップ表示のためにラベルは持っておく。
 */
const colorFamilyLabelMap: { [name: string]: string } = {
  brown: 'ブラウン',
  beige: 'ベージュ',
  hazel: 'ヘーゼル',
  gray: 'グレー',
  black: 'ブラック',
  olive: 'オリーブ',
  green: 'グリーン',
  navy: 'ネイビー',
  blue: 'ブルー',
  pink: 'ピンク',
  purple: 'パープル',
  red: 'レッド',
  yellow: 'イエロー',
  orange: 'オレンジ',
  white: 'ホワイト',
  clear: 'クリア',
  special: '特殊',
  suikou: '水光',
}

/**
 * カラー絞り込みの選択肢一覧。
 * 選択肢の集合と並び順は、既存のカラコン検索(SearchContactLens)の絞り込みUIと同じく
 * behavior(colorFamily.enabledNames)の定義順に従う。
 *
 * BFFにはカラー候補API(color-family-candidates。front側のproxyも /api/hl-proxy 配下に登録済み)が
 * 存在するが、**意図的に使わない**(今後も候補APIへ寄せる予定はない):
 * - 候補は動的でなくBFF内のハードコード定義で、「すべて」(空グループ)や「水光/特殊」(1ラベル=複数値)を含む
 *   独自構造(pj-hotel-lovers-api src/products/types/color-family.type.ts)
 * - iOSアプリの絞り込み画面も候補APIを使わず、アプリ内静的定義(ColorFamily enum、1値=1ラベル、
 *   水光はカラーオプションへ分離)で表示しており、候補APIに従うことがアプリとの整合にならない
 * - webは既存SearchContactLensのカラーUIと供給源(behavior)ごと揃えることを優先する
 */
export function colorFamilyOptions(): ColorFamilyOption[] {
  return BehaviorConfig.forHost.colorFamily.enabledNames
    .filter(name => name !== 'suikou' && colorFamilyLabelMap[name] !== undefined)
    .map(name => ({ value: name, label: colorFamilyLabelMap[name], iconName: name }))
}

/**
 * カラー系統名のユーザー向けラベル(未知の値はそのまま返す)。
 */
export function colorFamilyLabel(value: string): string {
  return colorFamilyLabelMap[value] !== undefined ? colorFamilyLabelMap[value] : value
}
